import React, { ReactNode } from "react";
import Tabs from "@/components/Tabs";
import Pagination from "@/components/BetterPagination";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import Link from "next/link";
import classNames from "classnames";
import { getHost, getSubjectListUrl } from "@/utils/URL";
import List from "./List";
import SubjectListHelmet from "./SubjectListHelmet";
import styles from "./index.module.scss";
import { goPcmSeoCrTopicList, GoPcmSeoCrTopicListResponse } from "@/api";
import to from "await-to-js";
import Header from "@/components/Layout/Header";
import ResponsiveWrap from "@/components/Layout/ResponsiveWrap";

const LETTERS_MAP = [..."abcdefghijklmnopqrstuvwxyz".split("")];
const pageSize = 180;

const letterToKeyword = (letter: string) => {
  return letter === "#" ? "other" : letter;
};

const keywordToLetter = (keyword?: string) => {
  if (!keyword) {
    return undefined;
  }
  return keyword === "other" ? "#" : keyword;
};

const SubjectList = (
  props: InferGetServerSidePropsType<typeof subjectListGetServerSideProps>
) => {
  const { data, cate: _cate, pageIndex } = props;
  const { keywords = [], total = 0, isLast = false } = data ?? {};
  const cate = _cate ? keywordToLetter(_cate) : "";

  return (
    <div className={classNames(styles.container)}>
      <SubjectListHelmet
        tabName={cate}
        pageIndex={pageIndex}
        list={keywords.slice(0, 3).map((keyword) => keyword.searchWord)}
      />
      <Header />
      <ResponsiveWrap
        className={classNames(styles.responsiveWrap, "pt16 pb36 pr")}
        maxWidth={932}
      >
        <Tabs
          tabBarGutter={4}
          defaultActiveKey={cate}
          disableShowDefault={!cate}
          tabBarClassName={styles.tabBarItem}
          items={LETTERS_MAP.map((letter) => {
            const active = letter === cate;
            return {
              key: letter,
              // eslint-disable-next-line no-script-url
              label: (
                <Link
                  href={
                    active
                      ? "javascript:;"
                      : getHost() + getSubjectListUrl(letterToKeyword(letter))
                  }
                >
                  {letter}
                </Link>
              ),
              children: null,
            };
          })}
        />
        <div className={styles.subjectList}>
          <List list={keywords} />
        </div>
        {!!cate && (
          <Pagination
            className={styles.pagination}
            pageSize={pageSize}
            total={total}
            current={pageIndex}
            itemRender={(
              page: number,
              type: string,
              originalElement: ReactNode
            ) => {
              const activePage = page === +pageIndex;
              const noPrev = page == 0 && type === "prev";
              const noNext = isLast && type === "next";
              const noLink = noPrev || noNext;
              return (
                <Link
                  href={
                    noLink
                      ? "javascript:;"
                      : `${getHost()}${getSubjectListUrl(cate, page)}`
                  }
                >
                  {originalElement}
                </Link>
              );
            }}
          />
        )}
      </ResponsiveWrap>
    </div>
  );
};

export const subjectListGetServerSideProps: GetServerSideProps<{
  data: GoPcmSeoCrTopicListResponse;
  cate: string;
  pageIndex: number;
}> = async (ctx) => {
  const { query } = ctx;
  let cate = query.cate as string;
  let pageIndex = "1";

  if (cate) {
    const [_letter, _pageIndex] = cate.split("_");
    cate = letterToKeyword(_letter);
    pageIndex = _pageIndex ?? pageIndex;
  }

  const [err, data] = await to(
    goPcmSeoCrTopicList({ cate, pageIndex, pageSize: `${pageSize}` }, {}, ctx)
  );

  if (err) {
    return {
      redirect: {
        statusCode: 302,
        destination: "/",
      },
    };
  }

  if (!data?.keywords?.length || (data.pages && data.pages < +pageIndex)) {
    return {
      redirect: {
        statusCode: 302,
        destination: "/",
      },
    };
  }

  return {
    props: {
      data,
      cate: typeof cate === "undefined" ? "" : cate,
      pageIndex: +pageIndex,
    },
  };
};

export default SubjectList;
